import { produce } from 'immer';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  parseAsArrayOf,
  parseAsString,
  useQueryStates,
} from 'nuqs';
import React, { useRef, useTransition } from 'react';

import { DealershipsByProvince } from '@/services/filters/new-filters.service';
import { ParentFilter } from '@/types/filters/filters.types';

import { NestedCheckboxesSimple } from '../../../nested-checkboxes-simple/nested-checkboxes';
import { isProvinceLandingPage, useLandingAwareClear } from '../../hooks/useLandingAwareClear';
import { NewFilter } from '../new-filter/new-filter';
import { useQueryNonce, useResetPage } from '../utils';

export const NewLocation: React.FC<{
  dealershipsByProvince: DealershipsByProvince;
  count: number;
  defaultLocation?: string;
  defaultDealershipId?: string;
  searchQuery?: string;
  setSearchQuery?: (value: string | null) => void;
}> = ({
  dealershipsByProvince = [],
  count,
  defaultLocation,
  defaultDealershipId,
  // searchQuery,
  setSearchQuery,
}) => {
  const [_, startTransition] = useTransition();
  const { generate } = useQueryNonce();
  const resetPage = useResetPage();
  const landingAwareClear = useLandingAwareClear();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // const autoSelectedProvinceRef = useRef<Set<string>>(new Set());
  // const autoSelectedDealershipRef = useRef<Set<string>>(new Set());
  // const previousSearchQueryRef = useRef<string>();
  const searchClearedByUserRef = useRef(false);
  const [locations, setLocations] = useQueryStates(
    {
      dealership: parseAsArrayOf(parseAsString).withDefault(
        defaultDealershipId ? [defaultDealershipId] : [],
      ),
      province: parseAsArrayOf(parseAsString).withDefault(
        defaultLocation ? [defaultLocation.toLowerCase()] : [],
      ),
    },
    {
      history: 'push',
      startTransition,
      shallow: false,
    },
  );

  const syncedOptions = dealershipsByProvince.map((province) => {
    const provinceChecked = locations.province.some(
      (p) => p.toLowerCase() === province.name.toLowerCase(),
    );

    const dealerships = province.children.map((dealership) => ({
      ...dealership,
      isChecked: provinceChecked || locations.dealership.some(
        (id) => id.toLowerCase() === dealership.id.toLowerCase(),
      ),
    }));

    return {
      ...province,
      isChecked: provinceChecked,
      children: dealerships,
    };
  });

  /* --------------------------------------------------
   * AUTO-SELECTION FROM SEARCH QUERY
   * -------------------------------------------------- */

  // useEffect(() => {
  //   if (!searchQuery || !searchQuery.trim()) {
  //     if (!searchClearedByUserRef.current) {
  //       previousSearchQueryRef.current = searchQuery;
  //       return;
  //     }

  //     if (
  //       autoSelectedProvinceRef.current.size > 0 ||
  //       autoSelectedDealershipRef.current.size > 0
  //     ) {
  //       setLocations((prev) => ({
  //         province: prev?.province?.filter(
  //           (p) => !autoSelectedProvinceRef.current.has(p),
  //         ) ?? [],
  //         dealership: prev?.dealership?.filter(
  //           (d) => !autoSelectedDealershipRef.current.has(d),
  //         ) ?? [],
  //       }));

  //       autoSelectedProvinceRef.current.clear();
  //       autoSelectedDealershipRef.current.clear();
  //     }

  //     searchClearedByUserRef.current = false;
  //     previousSearchQueryRef.current = searchQuery;
  //     return;
  //   }

  //   if (previousSearchQueryRef.current === searchQuery) return;
  //   previousSearchQueryRef.current = searchQuery;

  //   const words = searchQuery
  //     .toLowerCase()
  //     .split(/\s+/)
  //     .filter(Boolean);

  //   const matchedProvinces = new Set<string>();
  //   const matchedDealerships = new Set<string>();

  //   dealershipsByProvince.forEach((province) => {
  //     const provinceMatch = words.some((w) =>
  //       province.name.toLowerCase() === w ||
  //       (w.length > 4 && province.name.toLowerCase().includes(w)),
  //     );

  //     if (provinceMatch) {
  //       matchedProvinces.add(province.name.toLowerCase());
  //       return;
  //     }

  //     province.children.forEach((dealer) => {
  //       const dealerMatch = words.some(
  //         (w) =>
  //           w.length > 2 &&
  //           (dealer.name.toLowerCase() === w ||
  //           (w.length > 4 && dealer.name.toLowerCase().includes(w))),
  //       );

  //       if (dealerMatch) {
  //         matchedDealerships.add(dealer.id.toLowerCase());
  //       }
  //     });
  //   });

  //   if (matchedProvinces.size === 0 && matchedDealerships.size === 0) return;

  //   setLocations((prev) => {
  //     const nextProvinces = new Set(prev?.province ?? []);
  //     const nextDealerships = new Set(prev?.dealership ?? []);

  //     matchedProvinces.forEach((p) => {
  //       if (!nextProvinces.has(p)) {
  //         nextProvinces.add(p);
  //         autoSelectedProvinceRef.current.add(p);
  //       }
  //     });

  //     matchedDealerships.forEach((d) => {
  //       if (!nextDealerships.has(d)) {
  //         nextDealerships.add(d);
  //         autoSelectedDealershipRef.current.add(d);
  //       }
  //     });

  //     return {
  //       province: Array.from(nextProvinces),
  //       dealership: Array.from(nextDealerships),
  //     };
  //   });

  //   // Clear search query after auto-selection
  //   if ((matchedProvinces.size > 0 || matchedDealerships.size > 0) && setSearchQuery) {
  //     setSearchQuery(null);
  //   }
  // }, [searchQuery, dealershipsByProvince, setLocations, setSearchQuery]);

  const toggleFilter = (options: ParentFilter[], name: string) => {
    setSearchQuery?.(null);
    return produce(options, (draftOptions) => {
      draftOptions?.forEach((option) => {
        if (option.name === name) {
          option.isChecked = !option.isChecked;
          // set all children:
          option.children?.forEach((child) => {
            child.isChecked = option.isChecked;
          });
        } else {
          option.children?.forEach((child) => {
            if (child.name === name) {
              child.isChecked = !child.isChecked;
            }
          });
        }
      });
    });
  };

  const setOptionsState = async (options: ParentFilter[]) => {
    searchClearedByUserRef.current = false;

    // Reset page to 1 when location filters change
    resetPage();

    const dealerships: string[] = [];
    const provinces: string[] = [];

    options.forEach((province) => {
      const visibleDealerships =
        province.children?.filter((dealership) => 
          typeof dealership.vehicleCount !== 'number' || dealership.vehicleCount
        ) || [];
      const checkedDealerships =
        visibleDealerships.filter((dealership) => dealership.isChecked) || [];
      const totalDealerships = visibleDealerships.length;

      // Province explicitly selected
      if (province.isChecked && checkedDealerships.length === 0) {
        provinces.push(province.name.toLowerCase());
        return;
      }

      // All dealerships selected → collapse to province
      if (checkedDealerships.length === totalDealerships && totalDealerships > 0) {
        provinces.push(province.name.toLowerCase());
        return;
      }

      // Single dealership province → collapse to province
      if (totalDealerships === 1 && checkedDealerships.length === 1) {
        provinces.push(province.name.toLowerCase());
        return;
      }

      // Partial dealership selection
      if (checkedDealerships.length > 0) {
        dealerships.push(
          ...checkedDealerships.map((dealership) => dealership.id),
        );
      }
    });

    await setLocations({
      dealership: dealerships,
      province: provinces,
    });
  };

  return (
    <NewFilter
      label="Locations"
      count={count}
      onClear={() => {
        const isSearchPage = pathname.includes('/search');
        const hasLocationFilters =
          searchParams.has('province') || searchParams.has('dealership');

        if (isSearchPage) {
          setLocations(null);
          setSearchQuery?.(null);
          generate();
          return;
        }

        if (hasLocationFilters) {
          setLocations(null);
          setSearchQuery?.(null);
          return;
        }

        landingAwareClear('immediate');
      }}
    >
      <NestedCheckboxesSimple
        selectFilter={(option) => {
          setOptionsState(toggleFilter(syncedOptions, option.name));
        }}
        options={syncedOptions}
        setOptionsState={async (newStateUpdater) => {
          const res = newStateUpdater(syncedOptions);

          await setOptionsState(res);
        }}
        resetText="Reset Filters"
        selectAllText="All"
        hideText="Hide Dealerships"
        openText="Show Dealerships"
        allSelected={false}
        onReset={() => {
          if (isProvinceLandingPage(pathname)) {
            setTimeout(() => {
              landingAwareClear('immediate');
            }, 100);
          }
        }}
      />
    </NewFilter>
  );
};
